import styled, { css, keyframes } from 'styled-components';

type ColumnProps = {
  $width?: string;
  $minWidth?: string;
  $flex?: number;
};

const columnStyle = css<ColumnProps>`
  ${({ $width }) => $width && `width: ${$width};`}
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth};`}
  ${({ $flex }) => ($flex ? `flex: ${$flex};` : 'flex-shrink: 0;')}
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

export const TableContainer = styled.div`
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e1e2e4;
  overflow: hidden;
  background: #fff;
  font-family: var(--font-family-base);
`;

export const TableInner = styled.div<{ $minWidth?: string }>`
  width: 100%;
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth};`}
`;

export const TableHeader = styled.div`
  width: fit-content;
  min-width: 100%;
`;

export const TableHeaderRow = styled.div<{ $columns?: string; $gap?: string }>`
  display: ${({ $columns }) => ($columns ? 'grid' : 'flex')};
  align-items: center;
  padding: 16px 20px;
  width: fit-content;
  min-width: 100%;
  background-color: rgba(112, 115, 124, 0.05);
  border-bottom: 1px solid rgba(112, 115, 124, 0.16);
  ${({ $columns, $gap }) =>
    $columns && `grid-template-columns: ${$columns}; column-gap: ${$gap || '16px'};`}
`;

export const TableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TableRow = styled.div<{ $columns?: string; $gap?: string }>`
  display: ${({ $columns }) => ($columns ? 'grid' : 'flex')};
  align-items: center;
  padding: 16px 20px;
  width: fit-content;
  min-width: 100%;
  border-bottom: 1px solid rgba(112, 115, 124, 0.16);
  transition: background-color 0.2s ease;
  ${({ $columns, $gap }) =>
    $columns && `grid-template-columns: ${$columns}; column-gap: ${$gap || '16px'};`}

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: rgba(112, 115, 124, 0.02);
  }
`;

export const HeaderCell = styled.div<ColumnProps>`
  ${columnStyle};
  display: flex;
  align-items: center;
  overflow: hidden;
  min-width: 0;
`;

export const BodyCell = styled.div<ColumnProps>`
  ${columnStyle};
  display: flex;
  align-items: center;
  overflow: hidden;
  min-width: 0;
`;

export const HeaderText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: rgba(46, 47, 51, 0.88);
  letter-spacing: 0.02em;
  line-height: 138.5%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

export const CellText = styled.span`
  font-size: 16px;
  color: #171719;
  letter-spacing: 0.01em;
  line-height: 150%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

export const TableState = styled.div`
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

export const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid rgba(112, 115, 124, 0.16);
  border-top: 3px solid #171719;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export const StateIcon = styled.div`
  font-size: 24px;
`;

export const EmptyIcon = styled.div`
  font-size: 32px;
  opacity: 0.6;
`;

export const StateText = styled.span`
  font-size: 14px;
  color: rgba(55, 56, 60, 0.61);
  letter-spacing: 0.02em;
  text-align: center;
`;

export const EmptyText = styled(StateText)`
  font-size: 16px;
`;

export const RetryButton = styled.button`
  padding: 8px 16px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 8px;
  background: #fff;
  color: #171719;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  font-family: var(--font-family-base);
  transition: all 0.2s ease;
  margin-top: 8px;

  &:hover {
    background: rgba(112, 115, 124, 0.05);
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: nowrap;
`;

export const ActionButton = styled.button<{ $tone: 'danger' | 'warning' | 'primary' | 'neutral' | 'default' }>`
  padding: 7px 8px;
  border-radius: 8px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.03em;
  line-height: 133.4%;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: var(--font-family-base);
  white-space: nowrap;

  ${({ $tone }) =>
    $tone === 'danger' &&
    css`
      color: #e52222;

      &:hover {
        background-color: rgba(229, 34, 34, 0.05);
      }
    `}

  ${({ $tone }) =>
    $tone === 'warning' &&
    css`
      color: #f59e0b;

      &:hover {
        background-color: rgba(245, 158, 11, 0.05);
      }
    `}

  ${({ $tone }) =>
    $tone === 'primary' &&
    css`
      color: #296768;

      &:hover {
        background-color: rgba(41, 103, 104, 0.05);
      }
    `}

  ${({ $tone }) =>
    $tone === 'neutral' &&
    css`
      color: #2563eb;
      border-color: rgba(37, 99, 235, 0.3);

      &:hover {
        background-color: rgba(37, 99, 235, 0.05);
      }
    `}

  ${({ $tone }) =>
    $tone === 'default' &&
    css`
      color: rgba(46, 47, 51, 0.88);

      &:hover {
        background-color: rgba(112, 115, 124, 0.05);
      }
    `}

  @media (max-width: 1024px) {
    padding: 4px 6px;
    font-size: 11px;
  }

  @media (max-width: 480px) {
    padding: 3px 5px;
    font-size: 10px;
  }
`;

export const StatusBadge = styled.span<{ $tone: 'exposed' | 'hidden' }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  white-space: nowrap;

  ${({ $tone }) =>
    $tone === 'exposed'
      ? css`
          background-color: rgba(41, 103, 104, 0.1);
          color: #296768;
        `
      : css`
          background-color: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        `}
`;
